import { ErrorLabel } from '@ibsys/kit';
import { FC } from 'react';
import { type KmAttrType, KM_ATTR_DATA_TYPES } from '@/entities/ontology';
import {
  getKmStructFields,
  isKmAttrJsonInput,
  isKmAttrList,
  isKmAttrRef,
  type KmAttrFormRow,
  type KmAttrFormValue,
  type KmAttrTypeResolver,
} from './attrFormValue';
import { KmAttrChoiceInput } from './KmAttrChoiceInput';
import { KmAttrListInput } from './KmAttrListInput';
import { KmAttrRefInput } from './KmAttrRefInput';
import { KmAttrScalarInput } from './KmAttrScalarInput';

type Props = {
  attrType: KmAttrType;
  /** Поиск атрибута справочника по коду: типы полей структуры заданы кодами */
  resolveAttrType: KmAttrTypeResolver;
  value?: KmAttrFormValue;
  onChange?: (value: KmAttrFormValue) => void;
  disabled?: boolean;
  error?: string;
  placeholder?: string;
};

/**
 * Ввод значения атрибута: способ выбирается по типу атрибута. Значение-структура
 * разворачивается полями по её составу — каждое поле вводится этим же компонентом.
 */
export const KmAttrValueInput: FC<Props> = (props) => {
  const { attrType, resolveAttrType, value, onChange, disabled, error, placeholder } = props;
  const isSelectLike =
    attrType.dataType === KM_ATTR_DATA_TYPES.SELECT || attrType.dataType === KM_ATTR_DATA_TYPES.MULTISELECT;

  if (attrType.dataType === KM_ATTR_DATA_TYPES.STRUCT && !isKmAttrJsonInput(attrType)) {
    const rows = Array.isArray(value) ? (value as KmAttrFormRow[]) : [];

    const applyFieldChange = (code: string, fieldValue: KmAttrFormValue) => {
      onChange?.(rows.map((row) => (row.code === code ? { ...row, value: fieldValue } : row)));
    };

    return (
      <div className="flex flex-col gap-3">
        {getKmStructFields(attrType).map((field) => {
          const fieldAttrType = resolveAttrType(field.attrCode);
          if (!fieldAttrType) {
            return null;
          }

          return (
            <div key={field.attrCode} className="flex flex-col gap-1">
              <span className="text-sm text-muted-foreground">
                {field.isRequired ? `${fieldAttrType.name} *` : fieldAttrType.name}
              </span>
              <KmAttrValueInput
                attrType={fieldAttrType}
                resolveAttrType={resolveAttrType}
                value={rows.find((row) => row.code === field.attrCode)?.value}
                onChange={(next) => applyFieldChange(field.attrCode, next)}
                disabled={disabled}
                placeholder={placeholder}
              />
            </div>
          );
        })}

        {!!error && <ErrorLabel>{error}</ErrorLabel>}
      </div>
    );
  }

  if (isKmAttrList(attrType) && !isKmAttrJsonInput(attrType)) {
    if (isKmAttrRef(attrType)) {
      return (
        <KmAttrRefInput
          isMulti
          refEntityType={attrType.options?.refEntityType ?? ''}
          value={value}
          onChange={onChange}
          disabled={disabled}
          error={error}
          placeholder={placeholder}
        />
      );
    }

    if (isSelectLike) {
      return (
        <KmAttrChoiceInput
          isMulti
          choices={attrType.options?.choices ?? []}
          value={value}
          onChange={onChange}
          disabled={disabled}
          error={error}
          placeholder={placeholder}
        />
      );
    }

    return (
      <KmAttrListInput
        attrType={attrType}
        value={value}
        onChange={onChange}
        disabled={disabled}
        error={error}
        placeholder={placeholder}
      />
    );
  }

  return (
    <KmAttrScalarInput
      attrType={attrType}
      value={value}
      onChange={onChange}
      disabled={disabled}
      error={error}
      placeholder={placeholder}
    />
  );
};
